import Color from 'color';
import { Link, navigate } from 'gatsby';
import React, { useMemo, useState } from 'react'

import Search from './Search';
import MobileMenu from './MobileMenu';
import NewEmailBar from './NewEmailBar';
import Modal from '../../components/modal';
import UTIcon from '../../components/icons';
import Button from '../../components/button';
import Glossary from '../../components/glossary';
import { useAuthContext } from '../../context/auth';
import useWindowSize from '../../hooks/useWindowSize';
import { useCourseContext } from '../../context/course';
import { usePartnerContext } from '../../context/partner'

const ICON_SIZE = 20;

export default function AppHeader() {

    const { course } = useCourseContext();
    const { partner, colors } = usePartnerContext();
    const { student, working, loading, signOut, adminToolImpersonating } = useAuthContext();
    const { isTablet } = useWindowSize();

    const [emailForm, setEmailForm] = useState(false);

    const [showMenu, setShowMenu] = useState(false);
    const [showGlossary, setShowGlossary] = useState(false);

    const headerBgColor = colors?.secondary;
    const headerTextColor = colors?.secondaryText;
    const headerBorderColor = colors?.border || colors?.gray200 || Color(colors?.secondaryText).alpha(0.15).string();

    const headerLogo = useMemo(() => {
        if (partner?.logo?.url) {
            return <img src={partner.logo.url} alt={partner?.name} className={"h-[50px] py-2 object-contain"} />
        }
        return <h1 className={"text-lg font-semibold"}>
            {partner?.name}
        </h1>
    }, [partner]);

    return (
        <>
            <header
                className={"py-3 shadow-sm"}
                style={{
                    borderBottom: `1px solid ${headerBorderColor}`,
                    backgroundColor: headerBgColor,
                    color: headerTextColor
                }}
            >
                <div className={"flex space-x-6 items-center px-6"}>
                    <div className="flex-shrink-0">
                        <Link to={"/"} className="block">
                            {headerLogo}
                        </Link>
                    </div>
                    {adminToolImpersonating && <h2 className={"text-sm py-1.5 px-3 text-white font-semibold bg-red-600 rounded-md"}>IMPERSONATING: {student?.email}</h2>}
                    <div className={"flex-1"}>
                        {!isTablet && (
                            <nav className={"flex justify-end items-center space-x-1"}>
                                <Search />
                                {course && <HeaderButton onClick={() => { navigate(`/classroom/${course.slug}/glossary`) }} disabled={working || loading} ariaLabel="Open glossary">
                                    <UTIcon type={"glossary"} color={headerTextColor} size={ICON_SIZE} />
                                </HeaderButton>}
                                <HeaderButton onClick={() => { setEmailForm(!emailForm) }} disabled={working || loading} ariaLabel="Send message">
                                    <UTIcon type={"message"} color={headerTextColor} size={ICON_SIZE} />
                                </HeaderButton>
                                <HeaderButton onClick={() => { navigate("/my-account") }} disabled={working || loading} ariaLabel="My account">
                                    <UTIcon type={"user"} color={headerTextColor} size={ICON_SIZE} />
                                </HeaderButton>
                                <HeaderButton onClick={() => { navigate("/help/user-guide"); }} disabled={working || loading} ariaLabel="Help and user guide">
                                    <UTIcon type={"question"} color={headerTextColor} size={ICON_SIZE} />
                                </HeaderButton>
                                <HeaderButton disabled={working || loading} onClick={signOut} ariaLabel="Sign out">
                                    {(working || loading) && <UTIcon color={headerTextColor} type={"spinner2"} size={ICON_SIZE} className={"animate-spin"} /> || <UTIcon color={headerTextColor} type={"signout"} size={ICON_SIZE} />}
                                </HeaderButton>
                            </nav>
                        )}

                        {isTablet && (
                            <>
                                <div className={"flex justify-end items-center space-x-2"}>
                                    <TabletHeaderButton onClick={() => { setEmailForm(true) }} ariaLabel="Send message">
                                        <UTIcon type={"message"} color={headerTextColor} size={ICON_SIZE} />
                                    </TabletHeaderButton>
                                    <TabletHeaderButton onClick={() => { setShowMenu(true) }} ariaLabel="Open menu">
                                        <UTIcon type={"menu"} color={headerTextColor} size={ICON_SIZE} />
                                    </TabletHeaderButton>
                                </div>

                                {showMenu && <MobileMenu setShowGlossary={setShowGlossary} onClose={() => { setShowMenu(false) }} />}
                            </>
                        )}
                    </div>
                </div>

                <NewEmailBar emailForm={emailForm} setEmailForm={setEmailForm} />

            </header>
            <Modal
                header={"Glossary"}
                className={"h-full w-9/12 min-h-[75vh] min-w-[75vw]"}
                show={showGlossary}
                onClose={() => { setShowGlossary(false) }}
                showXButton
            >
                <Glossary course={course} />
            </Modal>
        </>
    )
}

// The header is secondary / secondaryText, so a hover tint has to come from
// secondaryText: a white wash on a white header does nothing, and recolouring
// the icon to the header's own colour makes it vanish. A translucent wash of
// the text colour reads as a subtle darken on light and lighten on dark.
const useHeaderHoverBackground = () => {
    const { colors } = usePartnerContext();
    return React.useMemo(
        () => Color(colors?.secondaryText || "#000").alpha(0.12).string(),
        [colors?.secondaryText]
    );
};

const HeaderButton = ({
    children,
    onClick,
    disabled,
    ariaLabel
}: {
    children: React.ReactNode,
    onClick: () => void,
    disabled?: boolean,
    ariaLabel?: string
}) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const hoverBg = useHeaderHoverBackground();

    return (
        <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Button
                variant={"ghost"}
                padding={"p-2"}
                className={`rounded-md transition-all duration-150 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                style={{ backgroundColor: isHovered && !disabled ? hoverBg : 'transparent' }}
                onClick={onClick}
                disabled={disabled}
                textColor={"secondaryText"}
                noShadow
                aria-label={ariaLabel}
            >
                {children}
            </Button>
        </div>
    )
}

const TabletHeaderButton = ({
    children,
    onClick,
    ariaLabel
}: {
    children: React.ReactNode,
    onClick: () => void,
    ariaLabel?: string
}) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const hoverBg = useHeaderHoverBackground();

    return (
        <button
            type={'button'}
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={"p-2 rounded-md transition-colors duration-150 flex items-center"}
            style={{ backgroundColor: isHovered ? hoverBg : 'transparent' }}
            aria-label={ariaLabel}
        >
            {children}
        </button>
    )
}